import { Conclusion, ReviewedParties } from "./AllMattersV2Overview";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { asList, asRecord, erased, hasValue } from "./AllMattersV2Fields";
import {
  KeyValues,
  PageLink,
  PartyTable,
  PartyName,
  RankBadge,
  Section,
  SourceLines,
  Tag,
  erasedTextSx,
  formatDate,
  receipt,
  text,
} from "./AllMattersV2Parts";

const Notes = ({ notes }: { notes: unknown[] }): JSX.Element | null =>
  notes.length ? (
    <Stack spacing={0.5}>
      {notes.map((note, index) => {
        const item = asRecord(note);
        const kind = text(item.種別);
        return (
          <Stack
            key={index}
            direction="row"
            gap={1}
            alignItems="baseline"
            flexWrap="wrap"
          >
            {kind && kind !== "その他" && <Tag label={kind} />}
            <Typography variant="body2" color="text.secondary">
              {typeof note === "string" ? note : text(item.原文)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  ) : null;

/** Indexes of the body lines: change records repeat heading, receipt and cause first. */
const trustBodyLines = (record: unknown): number[] => {
  const row = asRecord(record);
  const body = asList(asRecord(row.変更).本文の行).filter(
    (line): line is number => typeof line === "number"
  );
  return body.length ? body : asList(row.原文行).map((_, index) => index);
};

/**
 * A record whose whole body is underlined has been superseded or erased. The
 * heading lines of a change record stay un-underlined, so only the body counts.
 */
export const trustRecordErased = (record: unknown): boolean => {
  const erasures = asList(asRecord(record).原文行の抹消);
  const body = trustBodyLines(record);
  return body.length > 0 && body.every((index) => erasures[index] === true);
};

const TrustRecord = ({
  record,
  showErased,
  onShowPage,
}: {
  record: unknown;
  showErased: boolean;
  onShowPage: (page: number) => void;
}): JSX.Element => {
  const row = asRecord(record);
  const change = asRecord(row.変更);
  const changed = hasValue(row.変更);
  const body = trustBodyLines(row);
  const lines = asList(row.原文行);
  const erasures = asList(row.原文行の抹消);
  const recordErased = trustRecordErased(row);
  const parties = asList(row.当事者);
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 1.5,
        borderLeft: 4,
        borderLeftColor: recordErased
          ? theme.palette.text.disabled
          : changed
          ? theme.palette.secondary.main
          : theme.palette.info.main,
      })}
    >
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <RankBadge label={changed ? "変更" : "当初"} appendix={changed} />
        <Typography variant="body2" fontWeight={700}>
          {changed ? text(change.見出し) || "変更の記録" : "当初の記録"}
        </Typography>
        {recordErased && <Tag label="抹消事項" filled />}
        {asList(change.原因).map((cause, index) => (
          <Tag
            key={index}
            label={text(asRecord(cause).語) || text(cause)}
            tone="primary"
          />
        ))}
        {asList(change.注記).map((note, index) => (
          <Tag key={`note-${index}`} label={text(note)} tone="warning" />
        ))}
        {(hasValue(change.受付) || hasValue(change.付記日)) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: "auto", whiteSpace: "nowrap" }}
          >
            {[
              receipt(change.受付),
              change.付記日 ? `${formatDate(change.付記日)} 付記` : "",
            ]
              .filter(Boolean)
              .join("　")}
          </Typography>
        )}
      </Stack>
      <Stack spacing={1} mt={1}>
        {changed && (
          <KeyValues
            items={[
              ["原因", text(change.原因)],
              [
                "変更後",
                hasValue(change.対象者) ||
                hasValue(change.氏名) ||
                hasValue(change.名称) ||
                hasValue(change.法人番号の突合) ? (
                  <PartyName party={change} />
                ) : null,
              ],
            ]}
          />
        )}
        {parties.length > 0 && (
          <Box sx={recordErased ? { "& td": erasedTextSx } : undefined}>
            <PartyTable parties={parties} />
          </Box>
        )}
        <SourceLines
          lines={body.map((index) => lines[index])}
          erasures={body.map((index) => erasures[index])}
          showErased={showErased}
          label="原文"
          // Parties already show the body; keep their source text folded.
          collapsedLines={parties.length ? 0 : 6}
        />
        {asList(row.付随記載).map((note, index) => {
          const item = asRecord(note);
          return (
            <Typography key={index} variant="body2" color="text.secondary">
              {`${text(item.役割)}の${text(item.種別)}: ${text(item.原文)}`}
            </Typography>
          );
        })}
        <PageLink
          page={asRecord(row.位置).開始ページ}
          onShowPage={onShowPage}
        />
      </Stack>
    </Paper>
  );
};

export const TrustCatalogs = ({
  catalogs,
  views,
  showErased,
  onShowPage,
}: {
  catalogs: unknown[];
  views: unknown;
  showErased: boolean;
  onShowPage: (page: number) => void;
}): JSX.Element => (
  <Stack spacing={3}>
    <Alert severity="info">
      信託の記録を表示しています。信託条項の最新状態を判定したものではありません。
      <Typography component="span" variant="inherit" sx={erasedTextSx}>
        下線
      </Typography>
      は抹消事項です。
    </Alert>
    {catalogs.map((catalog, index) => {
      const item = asRecord(catalog);
      if (!showErased && erased(item)) return null;
      // The trust view points at its catalog by position in the response.
      const view = asRecord(
        asList(views).find(
          (entry) => asRecord(entry).目録 === `/権利部/信託目録/${index}`
        )
      );
      const parties = asRecord(view.当事者);
      return (
        <Stack key={index} spacing={2}>
          <Section
            title={`信託目録 第${text(item.番号) || "—"}号`}
            tone={erased(item) ? "default" : "info"}
            tags={erased(item) && <Tag label="信託抹消済み" filled />}
          >
            <KeyValues
              items={[
                ["受付", receipt(item.受付)],
                ["調製", formatDate(item.調製)],
              ]}
            />
            <Notes notes={asList(item.予備)} />
          </Section>
          {hasValue(view.当事者) && (
            <Conclusion
              title="現在の当事者（解析）"
              value={parties}
              empty="該当する当事者はありません。"
              onShowPage={onShowPage}
            >
              {(values) => (
                <ReviewedParties
                  values={values}
                  issues={asList(parties.問題)}
                  onShowPage={onShowPage}
                />
              )}
            </Conclusion>
          )}
          {asList(item.項目).map((section, sectionIndex) => {
            const group = asRecord(section);
            const records = asList(group.記録);
            const hidden = showErased
              ? 0
              : records.filter(trustRecordErased).length;
            return (
              <Section
                key={sectionIndex}
                title={text(group.項目名) || "信託の記録"}
                tone="secondary"
                tags={
                  hidden > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      抹消事項の記録 {hidden}件を非表示
                    </Typography>
                  )
                }
              >
                {records.map((record, recordIndex) =>
                  showErased || !trustRecordErased(record) ? (
                    <TrustRecord
                      key={recordIndex}
                      record={record}
                      showErased={showErased}
                      onShowPage={onShowPage}
                    />
                  ) : null
                )}
                {hidden > 0 && hidden === records.length && (
                  <Typography variant="body2" color="text.secondary">
                    抹消されていない記録はありません。
                  </Typography>
                )}
              </Section>
            );
          })}
        </Stack>
      );
    })}
  </Stack>
);
